import { useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useQueryClient } from '@zg-rentals/react-query5';

import { Flex, Legend, Spacer, Tag } from '@zillow/constellation';
import {
  Button,
  FieldSet,
  FormHelp,
  Heading,
  IconButton,
  Input,
  Label,
  Paragraph,
  Popover,
} from '@zillow/constellation-10';
import { IconLockClosedOutline } from '@zillow/constellation-icons';
import { logError } from '@zg-rentals/log-error';

import { ModularListingContext } from '../../../containers/CreateListingPage/context';

import { useSelector } from '../../../selectors/useSelector';

import PostingActions from '../../../actions/PostingActions';

import AnalyticsConstants from '../../../constants/AnalyticsConstants';
import { GET_LISTING_RECS } from '../../../constants/RecommendationsConstants';
import VisuallyHiddenScreenReader from '../../../components/VisuallyHiddenScreenReader';

import type { ChangeEvent } from 'react';
import type { ApartmentAmenityProps } from '../../../model/ApartmentAmenity';

const LOCK_BUTTON_TOOLTIP_CONTENT = {
  title: 'Need to edit these amenities?',
  body: 'These amenities are coming from your feed and must be edited in that system.',
};

const ADD_BUTTON_TOOLTIP_CONTENT = {
  title: 'Adding additional amenities',
  body: 'Added amenities will only display on Zillow Rental Network and must be edited or removed here.',
};

type TagProps = Omit<ApartmentAmenityProps, 'availableInSelectUnits'>;

type Props = {
  errorObj: { additionalAmenities?: string };
  isInputFormDisabled?: boolean;
  label: string;
  labelSize?: string;
  listingAlias: string;
  previouslySavedAmenities: Array<TagProps>;
  updateFormData: (arg: { propertyAmenities: Array<TagProps> }) => void;
  updateErrorObj: (arg: { additionalAmenities?: string }) => void;
  updateFetchingStatus?: (v: boolean) => void;
};

const AdditionalAmenities = ({
  errorObj,
  isInputFormDisabled = false,
  label,
  labelSize = 'md',
  listingAlias,
  previouslySavedAmenities = [],
  updateFormData,
  updateErrorObj,
  updateFetchingStatus = () => {},
}: Props): JSX.Element => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const {
    currentProperty: {
      isFeed,
      isMultifamily,
      restrictions: { isPropertyAmenitiesUpdateRestricted },
    },
  } = useSelector((state) => state.properties);

  const [tags, setTags] = useState([...previouslySavedAmenities]);
  const [tagToAdd, setTagToAdd] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [duplicateTagError, setDuplicateTagError] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const hasTags = tags.length > 0;

  const {
    modularListingState: { isInModalState },
  } = useContext(ModularListingContext);

  useEffect(() => {
    updateFormData({ propertyAmenities: tags });
  }, [tags]);

  const addNewTagOnChangeHandler = (e: ChangeEvent<HTMLInputElement>) => {
    if (duplicateTagError) {
      setDuplicateTagError(false);
    }
    setTagToAdd(e.target.value);
  };

  const createAmenityOnBlurHandler = () => {
    if (tagToAdd === '') {
      updateErrorObj({ additionalAmenities: undefined });
    }
  };

  const createAmenity = () => {
    setSuccessMessage('');

    setDuplicateTagError(false);

    if (!tagToAdd) {
      updateErrorObj({ additionalAmenities: 'Property amenity cannot be blank.' });
      return;
    }
    updateErrorObj({ additionalAmenities: undefined });

    const isDuplicateOfSavedTag = tags.some((tag) => tag.amenity === tagToAdd);

    if (isDuplicateOfSavedTag) {
      setDuplicateTagError(true);
      return;
    }

    updateFetchingStatus(true);

    dispatch(
      PostingActions.property.amenity.create(
        {
          propertyId: listingAlias,
          amenityText: tagToAdd,
        },
        AnalyticsConstants.LISTING_COMPONENT_GROUP_NAME.AMENITIES,
      ),
    )
      // @ts-expect-error
      .then((result: { response: { amenityId: string } }) => {
        setSuccessMessage(`Successfully added ${tagToAdd}.`);
        updateErrorObj({ additionalAmenities: undefined });
        queryClient.invalidateQueries({ queryKey: [GET_LISTING_RECS] });
        setTags([...tags, { id: result.response.amenityId, amenity: tagToAdd, isEditable: true }]);
        setTagToAdd('');
      })
      .catch((error: { message: string }) => {
        updateErrorObj({ additionalAmenities: error.message });
        logError({
          error,
          context: 'PropertyAmenities.createAmenity',
        });
      })
      .finally(() => {
        updateFetchingStatus(false);
      });
  };

  const deleteAmenity = (index: number) => {
    setSuccessMessage('');

    const tagToDelete = tags[index];

    updateFetchingStatus(true);
    setIsDeleting(true);

    dispatch(PostingActions.property.amenity.delete(tagToDelete.id))
      // @ts-expect-error
      .then((response: { success: boolean; error: { message: string } }) => {
        if (response.success) {
          setSuccessMessage(`Successfully deleted ${tagToDelete.amenity}.`);
          queryClient.invalidateQueries({ queryKey: [GET_LISTING_RECS] });
          const tagsCopy = [...tags];
          tagsCopy.splice(index, 1);
          setTags(tagsCopy);
        } else {
          const clientErrMsg = 'There was an error deleting your amenity. Please try again.';
          updateErrorObj({ additionalAmenities: clientErrMsg });
          logError({
            error: response.error,
            errorType: 'AdditionalAmenitiesError',
            context: 'PropertyAmenities.deleteAmenity',
          });
        }
      })
      .finally(() => {
        updateFetchingStatus(false);
        setIsDeleting(false);
      });
  };

  const handleCreateAmenityKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const keyCode = e.which || e.keyCode;
    if (keyCode === 13 && isInModalState) {
      createAmenity();
    }
  };

  return (
    <FieldSet
      legend={
        <>
          {hasTags ? (
            <Flex display="flex" flexDirection="row" gap="xs" alignItems="center" marginBottom="sm">
              <Legend size={labelSize} disabled={isInputFormDisabled}>
                {label}
              </Legend>

              {isFeed && (
                <Popover
                  header={
                    <Heading level={2} textStyle="body-lg-bold">
                      {LOCK_BUTTON_TOOLTIP_CONTENT.title}
                    </Heading>
                  }
                  body={<Paragraph>{LOCK_BUTTON_TOOLTIP_CONTENT.body}</Paragraph>}
                  placement="top"
                  trigger={
                    <IconButton
                      size="sm"
                      title="Lock outline"
                      tone="neutral"
                      emphasis="bare"
                      icon={<IconLockClosedOutline />}
                    />
                  }
                />
              )}
            </Flex>
          ) : (
            <Spacer marginBottom="sm" />
          )}
        </>
      }
    >
      {hasTags && (
        <ul>
          {tags.map((tag, i) =>
            !tag.isEditable ? (
              <li key={`feed-amenity-tag-${i}`}>
                <Paragraph css={{ marginBottom: '200', color: 'gray.400' }}>{tag.amenity}</Paragraph>
              </li>
            ) : null,
          )}
          <Spacer marginBottom="xs" />
        </ul>
      )}

      {hasTags && (
        <ul>
          {tags.map((tag, i) => (
            <li key={`amenity-tag-${i}`}>
              {/* TODO: create delete queue */}
              <Tag textTransform="none" isOpen={tag.isEditable} onClose={() => (isDeleting ? null : deleteAmenity(i))}>
                {tag.amenity}
              </Tag>
              <Spacer marginBottom="sm" />
            </li>
          ))}
        </ul>
      )}

      {Boolean(!isInputFormDisabled || (!isPropertyAmenitiesUpdateRestricted && isFeed)) && (
        <Flex display="column">
          <Label htmlFor="additionalAmenitiesInput">
            <FormHelp css={{ paddingBottom: '100' }}>Describe amenity</FormHelp>
          </Label>
          <Flex display="flex" gap="sm">
            <Input
              size="sm"
              placeholder="Example: Pet area"
              onChange={addNewTagOnChangeHandler}
              onKeyUp={handleCreateAmenityKeyUp}
              onBlur={createAmenityOnBlurHandler}
              value={tagToAdd}
              error={!!(duplicateTagError || errorObj.additionalAmenities)}
              id="additionalAmenitiesInput"
              aria-describedby={
                duplicateTagError || errorObj.additionalAmenities ? 'additionalAmenitiesError' : undefined
              }
              fluid
              css={{
                maxWidth: '340px',
                '&::placeholder': {
                  fontSize: '14px',
                },
                md: {
                  width: '75%',
                },
              }}
              aria-invalid={duplicateTagError || errorObj.additionalAmenities ? 'true' : undefined}
            />

            <Flex display="flex" flexDirection="row" gap="xs">
              <Button
                size="sm"
                tone={isMultifamily ? 'brand' : 'neutral'}
                emphasis="outlined"
                onClick={createAmenity}
                onBlur={createAmenityOnBlurHandler}
                disabled={!tagToAdd}
                data-testid="AdditionalAmenities-AddButton"
                aria-label={`Add ${tagToAdd || 'amenity'} to property`}
              >
                Add
              </Button>

              {isFeed && (
                <Popover
                  header={
                    <Heading level={2} textStyle="body-lg-bold">
                      {ADD_BUTTON_TOOLTIP_CONTENT.title}
                    </Heading>
                  }
                  body={<Paragraph>{ADD_BUTTON_TOOLTIP_CONTENT.body}</Paragraph>}
                  placement="top"
                />
              )}
            </Flex>
          </Flex>

          <VisuallyHiddenScreenReader errorMessage={errorObj.additionalAmenities} successMessage={successMessage} />
        </Flex>
      )}
      {(duplicateTagError && (
        <FormHelp id="additionalAmenitiesError" error={true}>
          Unable to save duplicate tags
        </FormHelp>
      )) ||
        (errorObj.additionalAmenities && (
          <FormHelp id="additionalAmenitiesError" error={!!errorObj.additionalAmenities}>
            errorObj.additionalAmenities
          </FormHelp>
        ))}
      <Spacer marginY="md" />
    </FieldSet>
  );
};

export default AdditionalAmenities;
